import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeComponentSizeType } from 'native-base/lib/typescript/components/types';
import { filter, find, get, keys, merge, reduce, sortBy } from 'lodash';

import { BrandDto, ModuleConfig, NavAppTypeSectionsDto, NavSectionSubModuleDto, StringTranslation } from '@pimm/services/lib/sms-tenants';

export interface AppSidebar {
  collapsed: boolean;
}

export type ModuleScreen<P = any> = {
  name: string;
  component?: React.ElementType;
  title?: string;
  titleTranslations?: StringTranslation[];
  subModules?: NavSectionSubModuleDto[];
  active?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  icon?: any;
  iconProps?: {
    size?: ThemeComponentSizeType<'Icon'>;
  };
  items?: ModuleScreen<P>[];
  identifierId?: string;
  moduleType?: string;
  payload?: P;
  seq?: number;
  showAsWidget?: boolean;
};

export interface AppModulesContextProps {
  sidebar: AppSidebar;
  screens: ModuleScreen[];
  widgets: ModuleScreen[];
  toggleSidebar: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const AppModulesContext = createContext<AppModulesContextProps>(undefined!);

// TODO: fix @typescript-eslint/explicit-module-boundary-types
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export type AppModulesProviderProps = {
  brand?: BrandDto;
  children: React.ReactNode;
  config?: {
    sidebar?: { collapsed?: boolean } & Omit<AppSidebar, 'collapsed'>;
  };
  moduleConfig?: ModuleConfig;
  screens?: ModuleScreen[];
};

export const AppModulesProvider = ({ brand, children, config, moduleConfig, ...props }: AppModulesProviderProps) => {
  const [sidebar, setSidebar] = useState<AppSidebar>(merge({ collapsed: false, items: [] }, config?.sidebar));

  const { screens, widgets } = useMemo(() => {
    const navSections = find<NavAppTypeSectionsDto>(brand?.navSections, _ => _.appType?.toLowerCase() === 'dop')?.sections;
    const widgets: ModuleScreen[] = [];

    // Group screen by nav section and get the correct title and translation for each screen
    const flatMapScreens = sortBy(navSections, ['seq']).flatMap(section => {
      const sortedSections = sortBy(section.modules, ['seq']);
      const modules: ModuleScreen[] = [];

      for (const _module of sortedSections) {
        const screen = find(props.screens, ['identifierId', _module.identifier]);
        // If the module is not actually a registered screen

        if (screen) {
          const configs = screen.payload.hasConfigs;
          const paths = keys(configs);
          let visible = paths.length === 0;

          if (moduleConfig && !visible) {
            // Check moduleConfig if has path and value === true
            visible = paths.some(path => get(moduleConfig, path) === configs[path]);
          }

          if (visible) {
            const _screen = {
              ...screen,
              title: _module.title,
              titleTranslations: _module.titleTranslations,
              seq: _module.seq,
              showAsWidget: _module.showAsWidget,
              subModules: _module.subModules,
              payload: {
                ...screen.payload,
                sectionTitle: section.title,
                sectionTitleTranslations: section.titleTranslations,
                sectionSeq: section.seq,
              },
            };
            if (_module.showAsWidget) widgets.push(_screen as any);
            modules.push(_screen);
          }
          continue;
        }

        if (!_module.showInMenu && _module.showAsWidget) {
          const widget = {
            identifierId: _module.identifier,
            title: _module.title,
            titleTranslations: _module.titleTranslations,
            seq: _module.seq,
            showAsWidget: _module.showAsWidget,
            subModules: _module.subModules,
            payload: {
              sectionTitle: section.title,
              sectionTitleTranslations: section.titleTranslations,
              sectionSeq: section.seq,
            },
          };
          widgets.push(widget as any);
        }
      }

      return modules;
    });

    return { screens: flatMapScreens, widgets };
  }, [brand?.navSections, moduleConfig, props.screens]);

  const toggleSidebar = useCallback(() => {
    setSidebar(prev => ({ ...prev, collapsed: !prev?.collapsed }));
  }, []);

  return (
    <AppModulesContext.Provider
      value={{
        sidebar,
        screens,
        widgets,
        toggleSidebar,
      }}
    >
      {children}
    </AppModulesContext.Provider>
  );
};

export const AppModulesConsumer = AppModulesContext.Consumer;

export const useAppModules = () => useContext(AppModulesContext);
